import React, { useState, useEffect } from 'react';
import { Save, X, Users, UserPlus, UserMinus } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { organizationApi, userApi } from '../../services/adminApi';
import type { OrganizationFormDataData, SquareLocation } from '../../types/organization';

interface OrganizationAddEditProps {
  organizationId?: string;
  onNavigate: (page: string) => void;
}

export const OrganizationAddEdit: React.FC<OrganizationAddEditProps> = ({ 
  organizationId, 
  onNavigate 
}) => {
  const isEdit = !!organizationId;
  
  const [form, setForm] = useState<OrganizationFormData>({
    name: '',
    display_name: '',
    description: '',
    is_active: true,
    status: 'trial',
    max_users: 10,
    min_required_images: 3,
    image_ordering_patterns: [],
    square_environment: 'sandbox'
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [newMemberPermissions, setNewMemberPermissions] = useState({
    can_access_square: false,
    can_access_ai: false,
    can_manage_users: false,
    can_manage_tokens: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [squareLocations, setSquareLocations] = useState<SquareLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [squareConnectionStatus, setSquareConnectionStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [squareConnectionMessage, setSquareConnectionMessage] = useState<string>('');
  const [originalSquareToken, setOriginalSquareToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEdit) {
      fetchOrganization();
    }
  }, [organizationId]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await organizationApi.get(organizationId!);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch organization');
      }
      
      const data = response.data;

      // Store whether tokens exist based on backend flags
      setOriginalSquareToken(data.has_square_token ? '••••••••••••••••' : undefined);

      setForm({
        name: data.name || '',
        display_name: data.display_name || '',
        description: data.description || '',
        is_active: data.is_active ?? true,
        status: data.status || 'active',
        max_users: data.max_users || 10,
        min_required_images: data.min_required_images ?? 3,
        image_ordering_patterns: data.image_ordering_patterns || [],
        // For security, show masked tokens if they exist
        square_access_token: data.has_square_token ? '••••••••••••••••' : '',
        square_application_id: data.square_application_id || '',
        square_environment: data.square_environment || 'sandbox',
        square_location_id: data.square_location_id || '',
        square_online_store_url: data.square_online_store_url || '',
        ai_api_token: data.has_ai_token ? '••••••••••••••••' : '',
        ai_system_prompt: data.ai_system_prompt || '',
        ai_prompt_context: data.ai_prompt_context || '',
      });

      // If organization has a Square token and location set, show a message
      if (data.has_square_token && data.square_location_id) {
        setSquareConnectionStatus('valid');
        setSquareConnectionMessage('Square integration is configured. Enter a new token to update.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      // Prepare data for submission
      const submitData = { ...form };
      
      // Only send token if it was changed from the masked value
      if (isEdit && submitData.square_access_token === originalSquareToken) {
        // Don't send the masked token - backend will keep existing token
        delete submitData.square_access_token;
      }

      const response = isEdit
        ? await organizationApi.update(organizationId!, submitData)
        : await organizationApi.create(submitData);

      if (!response.success) {
        throw new Error(response.message || 'Failed to save organization');
      }

      onNavigate('admin-organizations');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof OrganizationFormData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Member management functions
  const fetchMembers = async () => {
    if (!organizationId) return;
    
    try {
      const response = await organizationApi.getMembers(organizationId);
      if (response.success && response.data) {
        setMembers(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await userApi.list();
      if (response.success && response.data) {
        // Filter out users who are already members
        const memberIds = members.map(m => m.user_id);
        const available = response.data.filter((user: any) => !memberIds.includes(user.id));
        setAvailableUsers(available);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId || !organizationId) return;
    
    try {
      const response = await organizationApi.addMember(organizationId, {
        user_id: parseInt(selectedUserId),
        role: 'MEMBER',
        ...newMemberPermissions,
      });

      if (response.success) {
        await fetchMembers();
        setShowAddMember(false);
        setSelectedUserId('');
        setNewMemberPermissions({ can_access_square: false, can_access_ai: false, can_manage_users: false, can_manage_tokens: false });
      } else {
        const errorMsg = typeof response.message === 'string' 
          ? response.message 
          : 'Failed to add member';
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Failed to add member:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to add member';
      alert(errorMsg);
    }
  };

  const handleTogglePermission = async (userId: number, permission: string, currentValue: boolean) => {
    if (!organizationId) return;

    try {
      const response = await organizationApi.updateMemberPermissions(organizationId, userId, {
        [permission]: !currentValue,
      });

      if (response.success) {
        await fetchMembers();
      } else {
        const errorMsg = typeof response.message === 'string'
          ? response.message
          : 'Failed to update permission';
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Failed to update permission:', err);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!organizationId) return;
    
    if (!confirm('Are you sure you want to remove this member?')) return;
    
    try {
      const response = await organizationApi.removeMember(organizationId, userId);
      
      if (response.success) {
        await fetchMembers();
      } else {
        const errorMsg = typeof response.message === 'string' 
          ? response.message 
          : 'Failed to remove member';
        alert(errorMsg);
      }
    } catch (err) {
      console.error('Failed to remove member:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to remove member';
      alert(errorMsg);
    }
  };


  // Fetch members when editing an organization
  useEffect(() => {
    if (isEdit && organizationId) {
      fetchMembers();
    }
  }, [organizationId]);

  // Fetch available users when showing add member dialog
  useEffect(() => {
    if (showAddMember) {
      fetchAvailableUsers();
    }
  }, [showAddMember, members]);

  const fetchSquareLocations = async () => {
    if (!form.square_access_token || form.square_access_token === originalSquareToken) {
      setSquareLocations([]);
      setSquareConnectionStatus('idle');
      setSquareConnectionMessage('Enter a new Square access token to test');
      return;
    }

    try {
      setLoadingLocations(true);
      setSquareConnectionStatus('idle');
      setSquareConnectionMessage('');
      
      // Always use the Square locations endpoint with credentials
      const response = await organizationApi.getSquareLocations({
        square_access_token: form.square_access_token,
        square_environment: form.square_environment,
      });
      
      if (response.success && response.data) {
        // Response.data contains the backend response which has its own success/data structure
        const backendResponse = response.data;
        console.log('Square locations received:', backendResponse);
        
        if (backendResponse.success && backendResponse.data) {
          // Token is VALID
          setSquareLocations(backendResponse.data);
          setSquareConnectionStatus('valid');
          
          if (backendResponse.data.length === 0) {
            setSquareConnectionMessage('Token is valid but no locations found. Please create a location in Square.');
          } else {
            setSquareConnectionMessage(`Token validated successfully. Found ${backendResponse.data.length} location${backendResponse.data.length !== 1 ? 's' : ''}.`);
            
            // If there's only one location, automatically select it
            if (backendResponse.data.length === 1) {
              handleChange('square_location_id', backendResponse.data[0].id);
            }
          }
        } else {
          // Token is INVALID
          setSquareLocations([]);
          setSquareConnectionStatus('invalid');
          setSquareConnectionMessage(backendResponse.message || 'Invalid Square credentials');
        }
      } else {
        // Network or other error
        setSquareLocations([]);
        setSquareConnectionStatus('invalid');
        setSquareConnectionMessage(response.message || 'Failed to connect to server');
      }
    } catch (err) {
      console.error('Failed to fetch Square locations:', err);
      setSquareLocations([]);
      setSquareConnectionStatus('invalid');
      setSquareConnectionMessage('Failed to connect to Square. Please check your network connection.');
    } finally {
      setLoadingLocations(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Organization' : 'Add Organization'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEdit ? 'Update organization settings and credentials' : 'Create a new tenant organization'}
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorMessage message={error} />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="my-organization"
                  pattern="[a-z0-9\-]+"
                  required
                  disabled={isEdit}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Unique identifier for this organization. Use lowercase letters, numbers, and hyphens only.
                  {isEdit && " This cannot be changed after creation."}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => handleChange('display_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Organization Name"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Brief description of the organization..."
                />
              </div>
            </div>
          </Card>

          {/* Settings */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">Settings</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Users
                </label>
                <input
                  type="number"
                  value={form.max_users}
                  onChange={(e) => handleChange('max_users', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="1000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Required Images
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={form.min_required_images}
                  onChange={(e) => handleChange('min_required_images', Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum image slots when adding products (1–10)
                </p>
              </div>

              {/* Image Ordering Patterns */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Upload Ordering Patterns
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Define filename patterns to automatically order uploaded images. When users upload files,
                  images will be sorted based on which pattern matches their filename.
                </p>

                <div className="space-y-2">
                  {(form.image_ordering_patterns || []).map((pattern, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <input
                            type="text"
                            placeholder="e.g., _front, _back"
                            value={pattern.pattern}
                            onChange={(e) => {
                              const newPatterns = [...(form.image_ordering_patterns || [])];
                              newPatterns[index] = { ...pattern, pattern: e.target.value };
                              handleChange('image_ordering_patterns', newPatterns);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            placeholder="Order (0 = first)"
                            value={pattern.order}
                            onChange={(e) => {
                              const newPatterns = [...(form.image_ordering_patterns || [])];
                              newPatterns[index] = { ...pattern, order: parseInt(e.target.value) || 0 };
                              handleChange('image_ordering_patterns', newPatterns);
                            }}
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newPatterns = (form.image_ordering_patterns || []).filter((_, i) => i !== index);
                          handleChange('image_ordering_patterns', newPatterns);
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label="Remove pattern"
                        title="Remove pattern"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                      const newPatterns = [...(form.image_ordering_patterns || []), { pattern: '', order: (form.image_ordering_patterns || []).length }];
                      handleChange('image_ordering_patterns', newPatterns);
                    }}
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                  >
                    + Add Pattern
                  </button>
                </div>

                <div className="mt-2 text-xs text-gray-500">
                  <strong>Example:</strong> Pattern "_front" with order 0 will make files like "kimono_front.jpg" appear first.
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Organization is active
                  </span>
                </label>
              </div>
            </div>
          </Card>

          {/* Members - Only show when editing an existing organization */}
          {isEdit && organizationId && (
            <Card>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Members
                </h2>
                <Button
                  type="button"
                  onClick={() => setShowAddMember(true)}
                  variant="outline"
                  size="sm"
                  icon={UserPlus}
                >
                  Add Member
                </Button>
              </div>

              {members.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No members assigned to this organization</p>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-medium">
                            {(member.user_full_name || member.user_email || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium">
                            {member.user_full_name || member.user_email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {member.user_email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1 flex-wrap">
                          {member.role === 'OWNER' && (
                            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">Owner</span>
                          )}
                          {member.role === 'ADMIN' && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">Admin</span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleTogglePermission(member.user_id, 'can_access_square', member.can_access_square)}
                            className={`px-2 py-1 text-xs rounded cursor-pointer transition-colors ${member.can_access_square ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                            title={`${member.can_access_square ? 'Disable' : 'Enable'} Square access`}
                          >
                            Square
                          </button>
                          <button
                            type="button"
                            onClick={() => handleTogglePermission(member.user_id, 'can_access_ai', member.can_access_ai)}
                            className={`px-2 py-1 text-xs rounded cursor-pointer transition-colors ${member.can_access_ai ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                            title={`${member.can_access_ai ? 'Disable' : 'Enable'} AI access`}
                          >
                            AI
                          </button>
                          <button
                            type="button"
                            onClick={() => handleTogglePermission(member.user_id, 'can_manage_tokens', member.can_manage_tokens)}
                            className={`px-2 py-1 text-xs rounded cursor-pointer transition-colors ${member.can_manage_tokens ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                            title={`${member.can_manage_tokens ? 'Disable' : 'Enable'} token management`}
                          >
                            Tokens
                          </button>
                        </div>
                        {member.role !== 'OWNER' && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="text-red-600 hover:text-red-700"
                            title="Remove member"
                          >
                            <UserMinus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Member Dialog */}
              {showAddMember && (
                <div className="mt-4 p-4 border-t space-y-3">
                  <div className="flex gap-3">
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a user...</option>
                      {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name || user.email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {([
                      { key: 'can_access_square', label: 'Square Access' },
                      { key: 'can_access_ai', label: 'AI Access' },
                      { key: 'can_manage_tokens', label: 'Manage Tokens' },
                      { key: 'can_manage_users', label: 'Manage Users' },
                    ] as const).map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newMemberPermissions[key]}
                          onChange={(e) => setNewMemberPermissions({
                            ...newMemberPermissions,
                            [key]: e.target.checked,
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={handleAddMember}
                      variant="primary"
                      size="sm"
                      disabled={!selectedUserId}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setShowAddMember(false);
                        setSelectedUserId('');
                        setNewMemberPermissions({ can_access_square: false, can_access_ai: false, can_manage_users: false, can_manage_tokens: false });
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}


          {/* Square Integration */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">Square Integration</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Environment
                </label>
                <select
                  value={form.square_environment}
                  onChange={(e) => handleChange('square_environment', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={form.square_access_token || ''}
                    onChange={(e) => handleChange('square_access_token', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Square Access Token"
                  />
                  <button
                    type="button"
                    onClick={fetchSquareLocations}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                    disabled={!form.square_access_token || loadingLocations}
                  >
                    {loadingLocations ? 'Loading...' : 'Test Connection'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {isEdit && originalSquareToken
                    ? 'Token exists. Leave as is to keep current token, or enter a new one to update.' 
                    : 'Token is encrypted and stored securely'}
                </p>
                
                {/* Show connection status */}
                {squareConnectionStatus !== 'idle' && (
                  <div className={`mt-2 p-2 rounded-lg ${
                    squareConnectionStatus === 'valid' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {squareConnectionStatus === 'valid' ? (
                        <>
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm text-green-800">✓ Valid Token</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm text-red-800">✗ Invalid Token</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs mt-1 ml-7 text-gray-700">
                      {squareConnectionMessage}
                    </p>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Application ID
                </label>
                <input
                  type="text"
                  value={form.square_application_id || ''}
                  onChange={(e) => handleChange('square_application_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Square Application ID"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Square Online Store URL
                </label>
                <input
                  type="url"
                  value={form.square_online_store_url || ''}
                  onChange={(e) => handleChange('square_online_store_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://squareup.com/store/yourstore"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Base URL for your Square online store. Used for QR code redirects to product pages.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                {loadingLocations ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg">
                    <LoadingSpinner size="sm" />
                    <span className="text-sm text-gray-500">Loading locations...</span>
                  </div>
                ) : squareLocations.length > 0 ? (
                  <select
                    value={form.square_location_id || ''}
                    onChange={(e) => handleChange('square_location_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a location</option>
                    {squareLocations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name || location.business_name || 'Unnamed Location'}
                        {location.status !== 'ACTIVE' && ` (${location.status})`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={form.square_location_id || ''}
                    onChange={(e) => handleChange('square_location_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      form.square_location_id 
                        ? `Location ID: ${form.square_location_id}` 
                        : originalSquareToken 
                          ? "Test connection to load locations" 
                          : "Enter Square Access Token first"
                    }
                    disabled={!form.square_access_token && !form.square_location_id}
                    readOnly={!!form.square_location_id && squareLocations.length === 0}
                  />
                )}
                {squareLocations.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Found {squareLocations.length} location{squareLocations.length !== 1 ? 's' : ''} for this Square account
                  </p>
                )}
                {form.square_location_id && squareLocations.length === 0 && originalSquareToken && (
                  <p className="text-xs text-gray-500 mt-1">
                    Location is configured. Test the connection to see available locations.
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              onClick={() => onNavigate('admin-organizations')}
              variant="outline"
              icon={X}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              disabled={saving}
            >
              {saving ? 'Saving...' : (isEdit ? 'Update Organization' : 'Create Organization')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};